import { useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import type { FilterDataOption } from '@/app/common/filters/core/filtersTypes.ts';
import LabelNode from '@/app/common/filters/types/shared/LabelNode.tsx';
import TsxCheckbox from '@/app/common/filters/types/shared/TsxCheckbox.tsx';
import { useFiltersUiData } from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';
import {
  Container,
  InputWrapper,
  SearchIcon,
  StyledInput,
  InputLabel,
  DropdownOverlay,
  OptionItem,
  NoResults,
} from '@/app/common/filters/types/shared/styled/FilterSearchList.styled.ts';

interface FilterSearchListProps {
  name: string;
  placeholder: string;
  noResultsText: string;
  options: FilterDataOption[];
  disabled?: boolean;
  onToggle: (value: string) => void;
}

const FilterSearchList = ({ name, placeholder, noResultsText, options, disabled, onToggle }: FilterSearchListProps) => {
  const { useEvoFilters } = useFiltersUiData();
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const selectedOptions = options.filter((o) => o.selected);
  const unselectedOptions = options.filter((o) => !o.selected);
  const isOptionDisabled = (option: FilterDataOption) => {
    const staysRemovable = !!useEvoFilters && !!option.selected;
    return !staysRemovable && (!!option.disabled || option.counter === 0);
  };

  const filteredOptions = inputValue
    ? unselectedOptions.filter((opt) =>
        opt.label
          .toString()
          .split(' ')
          .some((word) => new RegExp(`^${inputValue}`, 'i').test(word))
      )
    : unselectedOptions;

  const noResults = inputValue.length > 0 && filteredOptions.length === 0;

  const openDropdown = () => {
    if (disabled) return;
    setCurrentIndex(0);
    setIsOpen(true);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setInputValue('');
    setCurrentIndex(0);
  };

  useOnclickOutside(
    () => {
      if (isOpen) closeDropdown();
    },
    {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      refs: [overlayRef as any, containerRef as any],
    }
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setCurrentIndex(0);
  };

  const handleSelect = (option: FilterDataOption) => {
    if (disabled || isOptionDisabled(option)) return;
    onToggle(option.value);
    closeDropdown();
  };

  const handleDeselect = (option: FilterDataOption) => {
    if (disabled || isOptionDisabled(option)) return;
    onToggle(option.value);
  };

  const scrollOnFocus = (index: number) => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const item = overlay.querySelector<HTMLLIElement>(`[data-index="${index}"]`);
    if (item) {
      if (overlay.scrollHeight > overlay.clientHeight) {
        const scrollBottom = overlay.clientHeight + overlay.scrollTop;
        const itemBottom = item.offsetTop + item.offsetHeight;
        if (itemBottom > scrollBottom) {
          overlay.scrollTop = itemBottom - overlay.clientHeight;
        } else if (item.offsetTop < overlay.scrollTop) {
          overlay.scrollTop = item.offsetTop;
        }
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape' || e.key === 'Tab') {
      closeDropdown();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (isOpen && filteredOptions[currentIndex]) {
        handleSelect(filteredOptions[currentIndex]);
      } else if (!isOpen) {
        openDropdown();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        openDropdown();
        return;
      }
      const next = currentIndex === filteredOptions.length - 1 ? 0 : currentIndex + 1;
      setCurrentIndex(next);
      scrollOnFocus(next);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) return;
      const prev = currentIndex === 0 ? filteredOptions.length - 1 : currentIndex - 1;
      setCurrentIndex(prev);
      scrollOnFocus(prev);
    }
  };

  return (
    <Container ref={containerRef} disabled={disabled}>
      <InputWrapper onClick={openDropdown} aria-haspopup="listbox" aria-expanded={isOpen}>
        <SearchIcon category="UI Essentials" name="icon-magnifying-glass" size={19} color="iconPrimary" />
        <InputLabel hasValue={!!inputValue} isFocused={isOpen}>
          {placeholder}
        </InputLabel>
        <StyledInput
          ref={inputRef}
          id={`filter-search-${name}`}
          name={name}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={openDropdown}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          autoComplete="off"
          aria-autocomplete="list"
          aria-controls={`filter-search-listbox-${name}`}
          aria-activedescendant={
            isOpen && filteredOptions.length > 0 ? `filter-search-option-${name}-${currentIndex}` : undefined
          }
        />
      </InputWrapper>
      {selectedOptions.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {selectedOptions.map((option, index) => {
            const isDisabled = !!disabled || isOptionDisabled(option);
            return (
              <li key={option.value} style={{ listStyle: 'none', minWidth: 0, display: 'flex' }}>
                <TsxCheckbox
                  $evo={useEvoFilters}
                  checkbox={{
                    id: `${name}_selected_${index}`,
                    label: <LabelNode option={option} isDisabled={isDisabled} />,
                  }}
                  className="checkbox"
                  onChange={() => handleDeselect(option)}
                  labelPosition="end"
                  isChecked={true}
                  disabled={isDisabled}
                  fullWidth={true}
                />
              </li>
            );
          })}
        </ul>
      )}
      {isOpen && (
        <DropdownOverlay
          ref={overlayRef}
          id={`filter-search-listbox-${name}`}
          role="listbox"
          aria-label={placeholder}
          className="filter-search-dropdown"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {noResults && (
            <NoResults role="status" aria-live="polite">
              {noResultsText}
            </NoResults>
          )}
          {!noResults &&
            filteredOptions.map((option, index) => (
              <OptionItem
                key={option.value}
                id={`filter-search-option-${name}-${index}`}
                data-index={index}
                role="option"
                aria-selected={index === currentIndex}
                isSelected={false}
                isHighlighted={index === currentIndex}
                isDisabled={isOptionDisabled(option)}
                tabIndex={-1}
                onClick={() => handleSelect(option)}
              >
                <LabelNode option={option} isDisabled={isOptionDisabled(option)} />
              </OptionItem>
            ))}
        </DropdownOverlay>
      )}
    </Container>
  );
};

export default FilterSearchList;
